// Context/EditProductContext.tsx
import React, { createContext, useContext, useReducer, Dispatch, useEffect, useCallback } from "react";
import MakeRequest from '../Helpers/MakeRequest';

export type Product = Record<string, any>;
export type ProductType = Record<string, any>;

type EditProductsState = {
  products: Product[];
  productTypes: ProductType[];
  saveDisabled: boolean;
  isLoading: boolean;
  error?: string | null;
};

type EditProductsAction =
  | { type: "setProducts"; products: Product[] }
  | { type: "updateProduct"; index: number; patch: Partial<Product> }
  | { type: "addProduct"; product: Product }
  | { type: "removeProduct"; index: number }
  | { type: "setProductTypes"; productTypes: ProductType[] }
  | { type: "setSaveDisabled"; value: boolean }
  | { type: "startLoading" }
  | { type: "error"; message: string }
  | { type: "reset" };

const initialState: EditProductsState = {
  products: [],
  productTypes: [],
  saveDisabled: true,
  isLoading: false,
  error: null,
};

function reducer(state: EditProductsState, action: EditProductsAction): EditProductsState {
  switch (action.type) {
    case "startLoading":
      return { ...state, isLoading: true, error: null };
    case "error":
      return { ...state, isLoading: false, error: action.message };
    case "setProducts":
      return { ...state, products: [...action.products], isLoading: false };
    case "updateProduct": {
      const products = state.products.map((p, i) => (i === action.index ? { ...p, ...action.patch } : p));
      return { ...state, products };
    }
    case "addProduct":
      return { ...state, products: [...state.products, action.product] };
    case "removeProduct":
      return { ...state, products: state.products.filter((_, i) => i !== action.index) };
    case "setProductTypes":
      return { ...state, productTypes: [...action.productTypes], isLoading: false };
    case "setSaveDisabled":
      return { ...state, saveDisabled: action.value };
    case "reset":
      return initialState;
    default:
      return state;
  }
}

type Ctx = {
  state: EditProductsState;
  dispatch: Dispatch<EditProductsAction>;
  // helpers
  setProducts: (products: Product[]) => void;
  updateProduct: (index: number, patch: Partial<Product>) => void;
  addProduct: (product: Product) => void;
  removeProduct: (index: number) => void;
  setProductTypes: (types: ProductType[]) => void;
  setSaveDisabled: (v: boolean) => void;
  // data load
  loadAll: () => Promise<void>;
};

const EditProductsContext = createContext<Ctx | null>(null);

export const EditProductsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const loadAll = useCallback(async () => {
    try {
      dispatch({ type: "startLoading" });
      const response = await MakeRequest(null, `content/products`, 'get');
      const list = response?.data ?? [];
      const products = list.filter((p: any) => p.type !== "productTypes");
      const typesNode = list.find((p: any) => p.type === "productTypes");
      const productTypes = typesNode?.productTypes ?? [];
      dispatch({ type: "setProducts", products });
      dispatch({ type: "setProductTypes", productTypes });
      dispatch({ type: "setSaveDisabled", value: true });
    } catch (e: any) {
      dispatch({ type: "error", message: e?.message || "Failed to load products" });
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const value: Ctx = {
    state,
    dispatch,
    setProducts: (products) => dispatch({ type: "setProducts", products }),
    updateProduct: (index, patch) => dispatch({ type: "updateProduct", index, patch }),
    addProduct: (product) => dispatch({ type: "addProduct", product }),
    removeProduct: (index) => dispatch({ type: "removeProduct", index }),
    setProductTypes: (types) => dispatch({ type: "setProductTypes", productTypes: types }),
    setSaveDisabled: (v) => dispatch({ type: "setSaveDisabled", value: v }),
    loadAll,
  };

  return <EditProductsContext.Provider value={value}>{children}</EditProductsContext.Provider>;
};

export const useEditProducts = () => {
  const ctx = useContext(EditProductsContext);
  if (!ctx) throw new Error("useEditProducts must be used within EditProductsProvider");
  return ctx;
};
